import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import { lazy, useState, useEffect } from "react";

import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  Fade,
  Autocomplete,
  TextField,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { User } from "../../users/types/user";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import { useAuth } from "../../auth/contexts/AuthProvider";
import { useFunctions } from "../../core/contexts/FunctionsProvider";
import { UsersSysType } from "../hooks/useUtils";
import api from "../infra/api";

const CalendarApp = lazy(() => import("../../calendar/pages/CalendarApp"));

// Styled Components - Teams inspired design
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    margin: "16px",
    maxHeight: "calc(100vh - 32px)",
    maxWidth: "calc(100vw - 32px)",
    animation: "slideIn 0.3s ease-out",
  },
  "@keyframes slideIn": {
    "0%": {
      opacity: 0,
      transform: "scale(0.95) translateY(-10px)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1) translateY(0)",
    },
  },
}));

const HeaderBar = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #5b5fc7 0%, #4f52b2 50%, #464eb8 100%)",
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(90deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)",
    pointerEvents: "none",
  },
}));

const TitleSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

const CalendarIcon = styled(Box)({
  width: "36px",
  height: "36px",
  borderRadius: "8px",
  background: "rgba(255, 255, 255, 0.2)",
  backdropFilter: "blur(10px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.3)",
    transform: "scale(1.05)",
  },
});

const HeaderTitle = styled(Typography)({
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "16px",
  letterSpacing: "0.3px",
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
});

const HeaderSubtitle = styled(Typography)({
  color: "rgba(255, 255, 255, 0.8)",
  fontSize: "12px",
  fontWeight: 400,
});

const CenterSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flex: 1,
  justifyContent: "center",
  maxWidth: "400px",
  margin: "0 auto",
});

const StyledAutocomplete = styled(Autocomplete)({
  minWidth: "280px",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(10px)",
    borderRadius: "8px",
    color: "#fff",
    fontSize: "14px",
    height: "38px",
    transition: "all 0.2s ease",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.3)",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.5)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255, 255, 255, 0.8)",
      borderWidth: "2px",
    },
    "& .MuiAutocomplete-input": {
      padding: "6px 8px !important",
      color: "#fff",
      "&::placeholder": {
        color: "rgba(255, 255, 255, 0.7)",
        opacity: 1,
      },
    },
    "& .MuiSvgIcon-root": {
      color: "rgba(255, 255, 255, 0.8)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: "13px",
    "&.Mui-focused": {
      color: "#fff",
    },
  },
});

const ControlsSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const AddButton = styled(IconButton)(({ theme }) => ({
  color: "#ffffff",
  padding: "8px 16px",
  borderRadius: "6px",
  background: "rgba(255, 255, 255, 0.2)",
  transition: "all 0.2s ease",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.3)",
    transform: "scale(1.02)",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.9)",
  padding: "8px",
  borderRadius: "6px",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "rgba(196, 49, 75, 0.9)",
    color: "#ffffff",
  },
}));

const StyledDialogContent = styled(DialogContent)({
  padding: 0,
  margin: 0,
  background: "#f5f5f5",
  "& .MuiCard-root": {
    borderRadius: 0,
    boxShadow: "none",
    border: "none",
  },
  "& .MuiCardHeader-root": {
    display: "none",
  },
  "& .MuiDivider-root": {
    display: "none",
  },
});

type UserDialogProps = {
  onAdd?: (user: Partial<User>) => void;
  onClose: () => void;
  onUpdate?: (user: User) => void;
  open: boolean;
  title: string;
  processing: boolean;
};

const ModalCalendar = ({
  onAdd,
  onClose,
  onUpdate,
  open,
  title,
  processing,
}: UserDialogProps) => {
  const { userInfo } = useAuth();
  const { usersSys } = useFunctions();
  const [selectedUser, setSelectedUser] = useState<UsersSysType | null>(null);
  const [triggerAddEvent, setTriggerAddEvent] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  // Busca o perfil do usuário para verificar se é admin
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profileUrl = process.env.REACT_APP_API_URL + "api/profile-info";
        const response = await api(profileUrl);
        if (response.ok) {
          const data = await response.json();
          setIsAdmin(data.isAdmin === true);
        }
      } catch (error) {
        console.error("Erro ao buscar perfil:", error);
        // Fallback para verificação local
        const localIsAdmin =
          userInfo?.tipo === "AD" ||
          userInfo?.usuarios_tipo_id === 1 ||
          String(userInfo?.tipo).toUpperCase() === "AD";
        setIsAdmin(localIsAdmin);
      }
    };

    if (open) {
      fetchProfile();
    }
  }, [open, userInfo]);

  const handleAddNewEvent = () => {
    setTriggerAddEvent((prev) => prev + 1);
  };

  const today = new Date();
  const formattedDate = today.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  const handleUserChange = (
    event: React.SyntheticEvent,
    newValue: unknown
  ) => {
    setSelectedUser(newValue as UsersSysType | null);
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="calendar-dialog-title"
      fullScreen
      maxWidth="xl"
      TransitionComponent={Fade as any}
      transitionDuration={300}
    >
      <HeaderBar>
        <TitleSection>
          <CalendarIcon>
            <CalendarTodayRoundedIcon
              sx={{ color: "#fff", fontSize: "20px" }}
            />
          </CalendarIcon>
          <Box>
            <HeaderTitle>Agenda</HeaderTitle>
            <HeaderSubtitle>
              {formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)}
            </HeaderSubtitle>
          </Box>
        </TitleSection>

        {isAdmin && (
          <CenterSection>
            <PersonSearchRoundedIcon
              sx={{ color: "rgba(255,255,255,0.8)", fontSize: "20px" }}
            />
            <StyledAutocomplete
              size="small"
              options={usersSys || []}
              getOptionLabel={(option: any) => option.nomecomp || option.nome}
              value={selectedUser}
              onChange={handleUserChange}
              renderOption={(props, option: any) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                >
                  <Avatar
                    src={option.img_usuario || undefined}
                    sx={{ width: 28, height: 28, fontSize: "12px" }}
                  >
                    {option.nome?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                      {option.nomecomp || option.nome}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Consultar agenda de usuário..."
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              noOptionsText="Nenhum usuário encontrado"
              clearText="Limpar"
            />
          </CenterSection>
        )}

        <ControlsSection>
          <Tooltip title="Nova Agenda" arrow>
            <AddButton size="small" onClick={handleAddNewEvent}>
              <AddRoundedIcon sx={{ fontSize: "18px" }} />
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#fff",
                  display: { xs: "none", sm: "block" },
                }}
              >
                Nova Agenda
              </Typography>
            </AddButton>
          </Tooltip>
          <Tooltip title="Fechar" arrow>
            <CloseButton size="small" onClick={onClose}>
              <CloseRoundedIcon sx={{ fontSize: "20px" }} />
            </CloseButton>
          </Tooltip>
        </ControlsSection>
      </HeaderBar>

      <StyledDialogContent>
        <CalendarApp
          filterUserId={selectedUser ? Number(selectedUser.id) : undefined}
          triggerAddEvent={triggerAddEvent}
        />
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default ModalCalendar;
